import React, { useState, useEffect } from 'react';
import { Plus, Edit, Trash2, Search, Sprout, Leaf, Apple, Cherry, Carrot, Wheat, TreePine, FlowerIcon as Flower, Grape, LayoutGrid, List, IndianRupee, Clock, Package } from 'lucide-react';
import { Plant } from '../../types';
import { plantService } from '../../services/firebase';

const PlantManagement: React.FC = () => {
  const [plants, setPlants] = useState<Plant[]>([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingPlant, setEditingPlant] = useState<Plant | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('list');
  const [formData, setFormData] = useState({
    name: '',
    brand: '',
    variety: '',
    category: '',
    pricePerPlant: '',
    growthDuration: '',
    seedsPerTray: '126',
    seedsProvider: 'Company' as 'Farmer' | 'Company',
  });

  // Function to get appropriate icon for plant type
  const getPlantIcon = (plantName: string) => {
    const name = plantName.toLowerCase();
    if (name.includes('tomato') || name.includes('cherry')) return Cherry;
    if (name.includes('lettuce') || name.includes('spinach') || name.includes('cabbage')) return Leaf;
    if (name.includes('apple') || name.includes('fruit')) return Apple;
    if (name.includes('carrot') || name.includes('radish') || name.includes('beetroot')) return Carrot;
    if (name.includes('wheat') || name.includes('rice') || name.includes('grain')) return Wheat;
    if (name.includes('tree') || name.includes('pine')) return TreePine;
    if (name.includes('flower') || name.includes('rose') || name.includes('marigold')) return Flower;
    if (name.includes('grape') || name.includes('vine')) return Grape;
    return Sprout; // Default icon
  };

  useEffect(() => {
    const fetchPlants = async () => {
      try {
        setLoading(true);
        const plantsData = await plantService.getAllPlants();
        setPlants(plantsData);
      } catch (error) {
        console.error('Error fetching plants:', error);
        // You might want to show an error message to the user here
      } finally {
        setLoading(false);
      }
    };

    fetchPlants();
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Check for duplicate plant (same brand + variety + seedsPerTray combination)
    const duplicatePlant = plants.find(plant => {
      const isSameBrand = plant.brand.toLowerCase().trim() === formData.brand.toLowerCase().trim();
      const isSameVariety = (plant.variety || '').toLowerCase().trim() === formData.variety.toLowerCase().trim();
      const isSameTrayCapacity = plant.seedsPerTray === parseInt(formData.seedsPerTray);
      const isDuplicate = isSameBrand && isSameVariety && isSameTrayCapacity;

      // When editing, exclude the current plant from the check
      if (editingPlant && plant.id === editingPlant.id) {
        return false;
      }

      return isDuplicate;
    });

    if (duplicatePlant) {
      alert(`A plant with brand "${formData.brand}", variety "${formData.variety}", and ${formData.seedsPerTray} seeds/tray already exists: ${duplicatePlant.name}`);
      return;
    }

    const plantData = {
      name: formData.name,
      brand: formData.brand,
      variety: formData.variety,
      category: formData.category || undefined,
      pricePerPlant: parseFloat(formData.pricePerPlant),
      availability: 0, // Default availability to 0 since it's not collected during onboarding
      growthDuration: parseInt(formData.growthDuration),
      seedsPerTray: parseInt(formData.seedsPerTray),
      seedsProvider: formData.seedsProvider,
      isActive: true,
    };

    try {
      if (editingPlant) {
        // Update plant in Firebase
        await plantService.updatePlant(editingPlant.id, plantData);
        setPlants(plants.map(p => p.id === editingPlant.id ? { ...p, ...plantData } : p));
      } else {
        // Add plant to Firebase
        const newPlantId = await plantService.addPlant(plantData);
        const newPlant: Plant = {
          id: newPlantId,
          ...plantData,
          isActive: true,
          createdAt: new Date() as any,
        };
        setPlants([...plants, newPlant]);
      }
      resetForm();
    } catch (error) {
      console.error('Error saving plant:', error);
      // You might want to show an error message to the user here
    }
  };

  const handleEdit = (plant: Plant) => {
    setEditingPlant(plant);
    setFormData({
      name: plant.name,
      brand: plant.brand,
      variety: plant.variety || '',
      category: plant.category || '',
      pricePerPlant: plant.pricePerPlant.toString(),
      growthDuration: plant.growthDuration.toString(),
      seedsPerTray: plant.seedsPerTray.toString(),
      seedsProvider: plant.seedsProvider || 'Company',
    });
    setShowForm(true);
  };

  const handleDelete = async (plantId: string) => {
    if (window.confirm('Are you sure you want to delete this plant?')) {
      try {
        await plantService.deletePlant(plantId);
        setPlants(plants.filter(p => p.id !== plantId));
      } catch (error) {
        console.error('Error deleting plant:', error);
        // You might want to show an error message to the user here
      }
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      brand: '',
      variety: '',
      category: '',
      pricePerPlant: '',
      growthDuration: '',
      seedsPerTray: '126',
      seedsProvider: 'Company',
    });
    setEditingPlant(null);
    setShowForm(false);
  };

  const filteredPlants = plants.filter(plant =>
    plant.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    plant.brand.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (plant.variety && plant.variety.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      <div className="bg-gradient-to-r from-purple-500 to-purple-600 rounded-2xl p-8 text-white shadow-xl">
        <h1 className="text-3xl font-bold mb-2">Plant Management</h1>
        <p className="text-purple-100 text-lg">Manage your plant varieties and inventory</p>
      </div>
      
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <button
          onClick={() => setShowForm(true)}
          className="inline-flex items-center px-6 py-3 border border-transparent text-base font-medium rounded-xl shadow-lg text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 transition-all duration-200 transform hover:scale-105"
        >
          <Plus className="h-4 w-4 mr-2" />
          Add Plant
        </button>
      </div>

      {/* Search and View Toggle */}
      <div className="flex flex-col sm:flex-row gap-4">
        <div className="relative flex-1">
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
            <Search className="h-5 w-5 text-gray-400" />
          </div>
          <input
            type="text"
            placeholder="Search plants..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-1 focus:ring-green-500 focus:border-green-500"
          />
        </div>
        {/* View Toggle */}
        <div className="flex items-center bg-gray-100 dark:bg-gray-700 rounded-lg p-1">
          <button
            onClick={() => setViewMode('grid')}
            className={`flex items-center gap-2 px-3 py-2 rounded-md text-sm font-medium transition-all ${
              viewMode === 'grid'
                ? 'bg-white dark:bg-gray-600 text-purple-600 dark:text-purple-400 shadow-sm'
                : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white'
            }`}
          >
            <LayoutGrid className="h-4 w-4" />
            Grid
          </button>
          <button
            onClick={() => setViewMode('list')}
            className={`flex items-center gap-2 px-3 py-2 rounded-md text-sm font-medium transition-all ${
              viewMode === 'list'
                ? 'bg-white dark:bg-gray-600 text-purple-600 dark:text-purple-400 shadow-sm'
                : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white'
            }`}
          >
            <List className="h-4 w-4" />
            List
          </button>
        </div>
      </div>

      {/* Modern Plant Form Modal */}
      {showForm && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm overflow-y-auto h-full w-full z-50 py-4">
          <div className="relative mx-auto p-6 w-11/12 max-w-2xl shadow-2xl rounded-2xl bg-white dark:bg-gray-800 my-8">
            <div className="space-y-6">
              {/* Header */}
              <div className="flex items-center space-x-4">
                <div className="w-12 h-12 bg-gradient-to-br from-purple-500 to-purple-600 rounded-xl flex items-center justify-center">
                  <Sprout className="h-6 w-6 text-white" />
                </div>
                <div>
                  <h3 className="text-2xl font-bold text-gray-900 dark:text-white">
                    {editingPlant ? 'Edit Plant Variety' : 'Add New Plant Variety'}
                  </h3>
                  <p className="text-gray-600 dark:text-gray-400">
                    {editingPlant ? 'Update plant information' : 'Enter details for the new plant variety'}
                  </p>
                </div>
              </div>

              <form onSubmit={handleSubmit} className="space-y-6">
                {/* Plant Details Card */}
                <div className="bg-gradient-to-br from-gray-50 to-gray-100 dark:from-gray-700 dark:to-gray-800 rounded-xl p-6 border border-gray-200 dark:border-gray-600">
                  <h4 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center">
                    <Leaf className="h-5 w-5 mr-2 text-green-600" />
                    Plant Information
                  </h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Plant Name *
                      </label>
                      <input
                        type="text"
                        required
                        value={formData.name}
                        onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                        placeholder="e.g., Cherry Tomato"
                        className="block w-full text-sm border-2 border-gray-200 dark:border-gray-600 rounded-xl px-4 py-3 bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent shadow-sm transition-all"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Brand *
                      </label>
                      <input
                        type="text"
                        required
                        value={formData.brand}
                        onChange={(e) => setFormData({ ...formData, brand: e.target.value })}
                        placeholder="e.g., Syngenta, Seminis"
                        className="block w-full text-sm border-2 border-gray-200 dark:border-gray-600 rounded-xl px-4 py-3 bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent shadow-sm transition-all"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Variety *
                      </label>
                      <input
                        type="text"
                        required
                        value={formData.variety}
                        onChange={(e) => setFormData({ ...formData, variety: e.target.value })}
                        placeholder="e.g., Hybrid F1, Desi"
                        className="block w-full text-sm border-2 border-gray-200 dark:border-gray-600 rounded-xl px-4 py-3 bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent shadow-sm transition-all"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Category
                      </label>
                      <select
                        value={formData.category}
                        onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                        className="block w-full text-sm border-2 border-gray-200 dark:border-gray-600 rounded-xl px-4 py-3 bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent shadow-sm transition-all"
                      >
                        <option value="">Select Category</option>
                        <option value="Vegetables">Vegetables</option>
                        <option value="Fruits">Fruits</option>
                        <option value="Leafy Greens">Leafy Greens</option>
                        <option value="Herbs">Herbs</option>
                        <option value="Flowers">Flowers</option>
                        <option value="Root Vegetables">Root Vegetables</option>
                        <option value="Other">Other</option>
                      </select>
                    </div>
                  </div>
                </div>

                {/* Pricing & Growth Card */}
                <div className="bg-gradient-to-br from-green-50 to-emerald-50 dark:from-green-900/30 dark:to-emerald-900/30 rounded-xl p-6 border border-green-200 dark:border-green-700">
                  <h4 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center">
                    <Apple className="h-5 w-5 mr-2 text-green-600" />
                    Pricing & Growth Details
                  </h4>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Price per Plant (₹) *
                      </label>
                      <div className="relative">
                        <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500 dark:text-gray-400">₹</span>
                        <input
                          type="number"
                          step="0.01"
                          min="1"
                          max="1000"
                          required
                          value={formData.pricePerPlant}
                          onChange={(e) => {
                            const value = e.target.value;
                            // Allow values from 1 to 1000 with max 2 decimal places
                            const regex = /^\d{0,4}(\.\d{0,2})?$/;
                            if (value === '' || (regex.test(value) && (value === '' || parseFloat(value) <= 1000))) {
                              setFormData({ ...formData, pricePerPlant: value });
                            }
                          }}
                          placeholder="5.50"
                          className="block w-full text-sm border-2 border-gray-200 dark:border-gray-600 rounded-xl pl-8 pr-4 py-3 bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent shadow-sm transition-all"
                        />
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Growth Duration (days) *
                      </label>
                      <input
                        type="number"
                        min="15"
                        max="60"
                        required
                        value={formData.growthDuration}
                        onChange={(e) => {
                          if (e.target.value.length <= 3) {
                            setFormData({ ...formData, growthDuration: e.target.value });
                          }
                        }}
                        placeholder="30"
                        className="block w-full text-sm border-2 border-gray-200 dark:border-gray-600 rounded-xl px-4 py-3 bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent shadow-sm transition-all"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Seeds per Tray *
                      </label>
                      <input
                        type="number"
                        min="50"
                        max="500"
                        required
                        value={formData.seedsPerTray}
                        onChange={(e) => {
                          if (e.target.value.length <= 3) {
                            setFormData({ ...formData, seedsPerTray: e.target.value });
                          }
                        }}
                        placeholder="126"
                        className="block w-full text-sm border-2 border-gray-200 dark:border-gray-600 rounded-xl px-4 py-3 bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent shadow-sm transition-all"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Seeds Provider *
                      </label>
                      <select
                        required
                        value={formData.seedsProvider}
                        onChange={(e) => setFormData({ ...formData, seedsProvider: e.target.value as 'Farmer' | 'Company' })}
                        className="block w-full text-sm border-2 border-gray-200 dark:border-gray-600 rounded-xl px-4 py-3 bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent shadow-sm transition-all"
                      >
                        <option value="Company">Company</option>
                        <option value="Farmer">Farmer</option>
                      </select>
                    </div>
                  </div>
                </div>

                {/* Action Buttons */}
                <div className="flex justify-end gap-4 pt-4">
                  <button
                    type="button"
                    onClick={resetForm}
                    className="px-6 py-3 text-sm font-medium text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-xl hover:bg-gray-200 dark:hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500 transition-all"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="px-6 py-3 text-sm font-medium text-white bg-gradient-to-r from-purple-600 to-purple-700 border border-transparent rounded-xl hover:from-purple-700 hover:to-purple-800 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500 disabled:opacity-50 disabled:cursor-not-allowed transition-all shadow-lg"
                  >
                    <div className="flex items-center">
                      <Plus className="h-4 w-4 mr-2" />
                      {editingPlant ? 'Update Plant' : 'Add Plant'}
                    </div>
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* List View - Table Format (Default) */}
      {viewMode === 'list' && (
        <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-lg border border-gray-100 dark:border-gray-700 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
              <thead className="bg-gradient-to-r from-purple-50 to-indigo-50 dark:from-purple-900/30 dark:to-indigo-900/30">
                <tr>
                  <th scope="col" className="px-6 py-4 text-left text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wider">
                    Plant
                  </th>
                  <th scope="col" className="px-6 py-4 text-left text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wider">
                    Brand / Variety
                  </th>
                  <th scope="col" className="px-6 py-4 text-left text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wider">
                    Category
                  </th>
                  <th scope="col" className="px-6 py-4 text-left text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wider">
                    Price
                  </th>
                  <th scope="col" className="px-6 py-4 text-left text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wider">
                    Growth
                  </th>
                  <th scope="col" className="px-6 py-4 text-left text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wider">
                    Seeds/Tray
                  </th>
                  <th scope="col" className="px-6 py-4 text-left text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wider">
                    Stock
                  </th>
                  <th scope="col" className="px-6 py-4 text-right text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                {filteredPlants.map((plant) => {
                  const IconComponent = getPlantIcon(plant.name);
                  return (
                    <tr key={plant.id} className="hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <div className="w-10 h-10 bg-gradient-to-br from-green-500 to-emerald-600 rounded-xl flex items-center justify-center shadow-sm">
                            <IconComponent className="h-5 w-5 text-white" />
                          </div>
                          <div className="ml-3">
                            <div className="text-sm font-semibold text-gray-900 dark:text-white">{plant.name}</div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex flex-wrap gap-1">
                          <span className="inline-flex items-center px-2 py-0.5 text-xs font-medium bg-blue-100 text-blue-800 dark:bg-blue-900/50 dark:text-blue-300 rounded-full">
                            {plant.brand}
                          </span>
                          {plant.variety && (
                            <span className="inline-flex items-center px-2 py-0.5 text-xs font-medium bg-purple-100 text-purple-800 dark:bg-purple-900/50 dark:text-purple-300 rounded-full">
                              {plant.variety}
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {plant.category ? (
                          <span className="inline-flex items-center px-2.5 py-1 text-xs font-medium bg-orange-100 text-orange-800 dark:bg-orange-900/50 dark:text-orange-300 rounded-full">
                            {plant.category}
                          </span>
                        ) : (
                          <span className="text-gray-400 dark:text-gray-500">-</span>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <IndianRupee className="h-4 w-4 mr-1 text-green-500" />
                          <span className="text-sm font-bold text-green-600 dark:text-green-400">{plant.pricePerPlant}</span>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center text-sm text-gray-600 dark:text-gray-300">
                          <Clock className="h-4 w-4 mr-1 text-purple-500" />
                          {plant.growthDuration} days
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center text-sm text-gray-600 dark:text-gray-300">
                          <Package className="h-4 w-4 mr-1 text-gray-400" />
                          {plant.seedsPerTray}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`inline-flex items-center px-2.5 py-1 text-xs font-semibold rounded-full ${
                          plant.availability > 100
                            ? 'bg-green-100 text-green-800 dark:bg-green-900/50 dark:text-green-300'
                            : plant.availability > 50
                              ? 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/50 dark:text-yellow-300'
                              : 'bg-red-100 text-red-800 dark:bg-red-900/50 dark:text-red-300'
                        }`}>
                          {plant.availability} plants
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right">
                        <div className="flex justify-end space-x-2">
                          <button
                            onClick={() => handleEdit(plant)}
                            className="inline-flex items-center px-3 py-1.5 text-sm font-medium text-blue-600 dark:text-blue-400 bg-blue-50 dark:bg-blue-900/30 rounded-lg hover:bg-blue-100 dark:hover:bg-blue-900/50 transition-colors"
                          >
                            <Edit className="h-4 w-4 mr-1" />
                            Edit
                          </button>
                          <button
                            onClick={() => handleDelete(plant.id)}
                            className="inline-flex items-center px-3 py-1.5 text-sm font-medium text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-900/30 rounded-lg hover:bg-red-100 dark:hover:bg-red-900/50 transition-colors"
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Grid View - Modern Plant Cards */}
      {viewMode === 'grid' && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredPlants.map((plant) => {
            const IconComponent = getPlantIcon(plant.name);
            return (
              <div
                key={plant.id}
                className="group bg-white dark:bg-gray-800 rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1 border border-gray-100 dark:border-gray-700 overflow-hidden"
              >
                {/* Card Header with Icon */}
                <div className="bg-gradient-to-br from-green-50 to-emerald-50 dark:from-green-900/30 dark:to-emerald-900/30 p-6 border-b border-gray-100 dark:border-gray-700">
                  <div className="flex items-center space-x-4">
                    <div className="w-14 h-14 bg-gradient-to-br from-green-500 to-emerald-600 rounded-xl flex items-center justify-center shadow-lg group-hover:scale-110 transition-transform duration-300">
                      <IconComponent className="h-8 w-8 text-white" />
                    </div>
                    <div className="flex-1">
                      <h3 className="text-lg font-bold text-gray-900 dark:text-white group-hover:text-green-600 transition-colors">
                        {plant.name}
                      </h3>
                      <div className="flex flex-wrap gap-1 mt-1">
                        <span className="inline-flex items-center px-2 py-0.5 text-xs font-medium bg-blue-100 text-blue-800 dark:bg-blue-900/50 dark:text-blue-300 rounded-full">
                          {plant.brand}
                        </span>
                        {plant.variety && (
                          <span className="inline-flex items-center px-2 py-0.5 text-xs font-medium bg-purple-100 text-purple-800 dark:bg-purple-900/50 dark:text-purple-300 rounded-full">
                            {plant.variety}
                          </span>
                        )}
                        {plant.category && (
                          <span className="inline-flex items-center px-2 py-0.5 text-xs font-medium bg-orange-100 text-orange-800 dark:bg-orange-900/50 dark:text-orange-300 rounded-full">
                            {plant.category}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Card Content */}
                <div className="p-6">
                  <div className="grid grid-cols-2 gap-4 mb-6">
                    <div className="text-center p-3 bg-gray-50 dark:bg-gray-700 rounded-xl">
                      <div className="text-2xl font-bold text-green-600 dark:text-green-400">₹{plant.pricePerPlant}</div>
                      <div className="text-xs text-gray-500 dark:text-gray-400 font-medium">Per Plant</div>
                    </div>
                    <div className="text-center p-3 bg-gray-50 dark:bg-gray-700 rounded-xl">
                      <div className="text-2xl font-bold text-purple-600 dark:text-purple-400">{plant.growthDuration}</div>
                      <div className="text-xs text-gray-500 dark:text-gray-400 font-medium">Days Growth</div>
                    </div>
                  </div>

                  <div className="space-y-3 mb-6">
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-gray-600 dark:text-gray-400">Seeds per Tray:</span>
                      <span className="font-semibold text-gray-900 dark:text-white">{plant.seedsPerTray}</span>
                    </div>
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-gray-600 dark:text-gray-400">Available Stock:</span>
                      <span className={`font-semibold ${
                        plant.availability > 100
                          ? 'text-green-600 dark:text-green-400'
                          : plant.availability > 50
                            ? 'text-yellow-600 dark:text-yellow-400'
                            : 'text-red-600 dark:text-red-400'
                      }`}>
                        {plant.availability} plants
                      </span>
                    </div>
                  </div>

                  {/* Action Buttons */}
                  <div className="flex space-x-2">
                    <button
                      onClick={() => handleEdit(plant)}
                      className="flex-1 inline-flex items-center justify-center px-4 py-2.5 text-sm font-medium text-white bg-gradient-to-r from-blue-500 to-blue-600 border border-transparent rounded-xl shadow-sm hover:from-blue-600 hover:to-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-all duration-200 transform hover:scale-105"
                    >
                      <Edit className="h-4 w-4 mr-2" />
                      Edit
                    </button>
                    <button
                      onClick={() => handleDelete(plant.id)}
                      className="inline-flex items-center justify-center px-4 py-2.5 text-sm font-medium text-white bg-gradient-to-r from-red-500 to-red-600 border border-transparent rounded-xl shadow-sm hover:from-red-600 hover:to-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 transition-all duration-200 transform hover:scale-105"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {filteredPlants.length === 0 && (
        <div className="text-center py-12">
          <Sprout className="mx-auto h-12 w-12 text-gray-400" />
          <h3 className="mt-2 text-sm font-medium text-gray-900 dark:text-white">No plants found</h3>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
            {searchTerm ? 'Try adjusting your search terms' : 'Get started by adding your first plant'}
          </p>
        </div>
      )}
    </div>
  );
};

export default PlantManagement; 